import React, { useState, useEffect } from 'react';
import { Button, Space, Input, Toast, Card, Tabs, TabPane, Row, Col, Modal, InputNumber } from '@douyinfe/semi-ui';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import Preview from './Preview';
import { ColorPicker } from '../../components/ColorPicker';
import { ImageSelector } from '../../components/ImageSelector';
import LinkSelector from '../../components/LinkSelector';
import { IconPlus, IconDelete } from '@douyinfe/semi-icons';
import request from '../../utils/request';
import styles from './index.module.scss';

const XcxTheme = () => {
    const [loading, setLoading] = useState(false);
    const [config, setConfig] = useState({
        tabbar_style: 'default',
        menu: [],
        theme: {
            '--td-brand-color': '#0052d9',
            '--td-brand-light-color': '#e0ebff',
            '--td-sc-border-radius': '8px',
            '--td-sc-margin': '16px'
        },
        server_url: '',
        copyright: '',
        logo: ''
    });

    const presetMenus = {
        'shopping': [
            { text: '首页', icon: 't-icon-home', active_icon: 't-icon-home', url: '/pages/basic/m1/m1' },
            { text: '分类', icon: 't-icon-system-3', active_icon: 't-icon-system-3', url: '/pages/basic/m2/m2' },
            { text: '购物车', icon: 't-icon-cart', active_icon: 't-icon-cart', url: '/pages/basic/m3/m3' },
            { text: '我的', icon: 't-icon-user', active_icon: 't-icon-user', url: '/pages/basic/m4/m4' }
        ],
        'store': [
            { text: '首页', icon: 't-icon-home', active_icon: 't-icon-home', url: '/pages/basic/m1/m1' },
            { text: '点单', icon: 't-icon-cart', active_icon: 't-icon-cart', url: '/pages/basic/m2/m2' },
            { text: '我的', icon: 't-icon-user', active_icon: 't-icon-user', url: '/pages/basic/m3/m3' }
        ],
        'course': [
            { text: '首页', icon: 't-icon-home', active_icon: 't-icon-home', url: '/pages/basic/m1/m1' },
            { text: '课程', icon: 't-icon-course', active_icon: 't-icon-course', url: '/pages/basic/m2/m2' },
            { text: '我的', icon: 't-icon-user', active_icon: 't-icon-user', url: '/pages/basic/m3/m3' }
        ]
    };

    const handlePresetMenuSelect = (presetType) => {
        Modal.confirm({
            title: '确认使用预设菜单',
            content: '使用预设菜单将覆盖当前的菜单设置，是否继续？',
            onOk: () => {
                setConfig(prev => ({
                    ...prev,
                    menu: presetMenus[presetType]
                }));
                Toast.success('已应用预设菜单');
            }
        });
    };

    const handleImageSelect = (index, field, value) => {
        const iconValue = value;
        handleMenuChange(index, field, iconValue);
    };

    // Ensure menu is always an array
    useEffect(() => {
        if (!Array.isArray(config.menu)) {
            setConfig(prev => ({
                ...prev,
                menu: []
            }));
        }
    }, [config.menu]);

    const fetchConfig = async () => {
        setLoading(true);
        try {
            const response = await request.get('/go/sp/sp_cfg/get/', {
                params: {
                    ukey: 'xcx_0'
                }
            });
            if (response.errno === 0 && response.val) {
                const configData = {
                    ...JSON.parse(response.val)
                };
                console.log('ret configData:', configData);
                setConfig(configData);
            }
        } catch (error) {
            console.error('获取配置失败:', error);
            Toast.error('获取配置失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchConfig();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const { presetColors, ...submitData } = config;
            const response = await request.post('/go/sp/sp_cfg/set/', {
                ukey: 'xcx_0',
                val: JSON.stringify(submitData)
            });
            if (response.errno === 0) {
                Toast.success('保存成功');
            } else {
                Toast.error(response.errstr || '保存失败');
            }
        } catch (error) {
            console.error('保存失败:', error);
            Toast.error('保存失败');
        }
    };

    const handleInputChange = (field, value) => {
        setConfig(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleThemeChange = (field, value) => {
        setConfig(prev => ({
            ...prev,
            theme: {
                ...prev.theme,
                [field]: value
            }
        }));
    };

    const handleAddMenu = () => {
        const currentMenu = Array.isArray(config.menu) ? config.menu : [];
        const newMenu = [...currentMenu, { url: '', text: '', icon: '', active_icon: '' }];
        setConfig(prev => ({
            ...prev,
            menu: newMenu
        }));
    };

    const handleRemoveMenu = (index) => {
        Modal.confirm({
            title: '确认删除',
            content: '确定要删除这个菜单项吗？',
            onOk: () => {
                const newMenus = config.menu.filter((_, i) => i !== index);
                setConfig(prev => ({
                    ...prev,
                    menu: newMenus
                }));
                Toast.success('删除成功');
            }
        });
    };

    const handleDragEnd = (result) => {
        if (!result.destination) return;

        const items = Array.from(config.menu);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);

        setConfig(prev => ({
            ...prev,
            menu: items
        }));
    };

    const handleMenuChange = (index, field, value) => {
        const newMenus = [...config.menu];
        newMenus[index] = {
            ...newMenus[index],
            [field]: value
        };
        setConfig(prev => ({
            ...prev,
            menu: newMenus
        }));
        console.log('newMenus:', newMenus);
    };

    return (
        <div className={styles.xcxThemeContainer}>
            <Row gutter={24}>
                <Col span={12}>
                    <Card title="小程序主题设置">
                        <form onSubmit={handleSubmit}>
                            <Tabs type="line">
                                <TabPane tab="主题样式" itemKey="theme">
                                    <div style={{ marginBottom: 16 }}>
                                        <label style={{ display: 'block', marginBottom: 8 }}>底部导航样式</label>
                                        <select
                                            value={config.tabbar_style}
                                            onChange={e => handleInputChange('tabbar_style', e.target.value)}
                                            style={{ width: '100%', height: 32, borderRadius: 4, border: '1px solid #ccc' }}
                                        >
                                            <option value="default">默认</option>
                                            <option value="float">浮动</option>
                                            <option value="fold">折叠</option>
                                        </select>
                                    </div>
                                    <div style={{ marginBottom: 16 }}>
                                        <label style={{ display: 'block', marginBottom: 8 }}>预设颜色</label>
                                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                            {[
                                                { brand: '#0052d9', background: '#e0ebff' },
                                                { brand: '#1890ff', background: '#e6f7ff' },
                                                { brand: '#52c41a', background: '#f6ffed' },
                                                { brand: '#fadb14', background: '#fffbe6' },
                                                { brand: '#fa8c16', background: '#fff7e6' },
                                                { brand: '#f5222d', background: '#fff1f0' },
                                                { brand: '#722ed1', background: '#f9f0ff' },
                                                { brand: '#13c2c2', background: '#e6fffb' },
                                                { brand: '#eb2f96', background: '#fff0f6' },
                                                { brand: '#fa541c', background: '#fff2e8' },
                                                { brand: '#2f54eb', background: '#f0f5ff' },
                                                { brand: '#a0d911', background: '#fcffe6' },
                                                { brand: '#1d39c4', background: '#e6f4ff' },
                                                { brand: '#08979c', background: '#e6fffb' },
                                                { brand: '#531dab', background: '#f9f0ff' },
                                                { brand: '#c41d7f', background: '#fff0f6' },
                                                { brand: '#d4380d', background: '#fff2e8' },
                                                { brand: '#10239e', background: '#f0f5ff' },
                                                { brand: '#389e0d', background: '#f6ffed' },
                                                { brand: '#d4b106', background: '#fffbe6' }
                                            ].map((color, index) => (
                                                <div
                                                    key={index}
                                                    style={{
                                                        padding: 4,
                                                        borderRadius: 8,
                                                        background: '#fff',
                                                        border: `2px solid ${config.theme['--td-brand-color'] === color.brand
                                                            ? 'var(--semi-color-primary)'
                                                            : 'var(--semi-color-border)'}`,
                                                        cursor: 'pointer'
                                                    }}
                                                    onClick={() => {
                                                        handleThemeChange('--td-brand-color', color.brand);
                                                        handleThemeChange('--td-brand-light-color', color.background);
                                                    }}
                                                >
                                                    <div style={{
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: 6,
                                                        background: color.brand,
                                                        marginBottom: 4
                                                    }} />
                                                    <div style={{
                                                        width: 32,
                                                        height: 32,
                                                        borderRadius: 6,
                                                        background: color.background
                                                    }} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                    <div style={{ marginBottom: 16 }}>
                                        <label style={{ display: 'block', marginBottom: 8 }}>主题色</label>
                                        <ColorPicker
                                            value={config.theme['--td-brand-color']}
                                            onChange={color => handleThemeChange('--td-brand-color', color)}
                                        />
                                    </div>
                                    <div style={{ marginBottom: 16 }}>
                                        <label style={{ display: 'block', marginBottom: 8 }}>主题背景色</label>
                                        <ColorPicker
                                            value={config.theme['--td-brand-light-color']}
                                            onChange={color => handleThemeChange('--td-brand-light-color', color)}
                                        />
                                    </div>
                                    <div style={{ marginBottom: 16 }}>
                                        <label style={{ display: 'block', marginBottom: 8 }}>圆角大小</label>
                                        <InputNumber
                                            value={parseInt(config.theme['--td-sc-border-radius'])}
                                            onChange={value => handleThemeChange('--td-sc-border-radius', `${value}px`)}
                                            placeholder="请输入圆角大小"
                                            min={0}
                                            max={32}
                                            suffix="px"
                                            style={{ width: '100%' }}
                                            showClear={false}
                                            hideButtons={false}
                                        />
                                    </div>
                                    <div style={{ marginBottom: 16 }}>
                                        <label style={{ display: 'block', marginBottom: 8 }}>边距大小</label>
                                        <InputNumber
                                            value={parseInt(config.theme['--td-sc-margin'])}
                                            onChange={value => handleThemeChange('--td-sc-margin', `${value}px`)}
                                            placeholder="请输入边距大小"
                                            min={0}
                                            max={48}
                                            suffix="px"
                                            style={{ width: '100%' }}
                                            showClear={false}
                                            hideButtons={false}
                                        />
                                    </div>
                                </TabPane>
                                <TabPane tab="底部菜单" itemKey="menu">
                                    <div style={{ marginBottom: 16 }}>
                                        <Space>
                                            <Button type="primary" icon={<IconPlus />} onClick={handleAddMenu}>添加菜单项</Button>
                                            <Button onClick={() => handlePresetMenuSelect('shopping')}>购物商城</Button>
                                            <Button onClick={() => handlePresetMenuSelect('store')}>门店点单</Button>
                                            <Button onClick={() => handlePresetMenuSelect('course')}>知识付费</Button>
                                        </Space>
                                    </div>
                                    <DragDropContext onDragEnd={handleDragEnd}>
                                        <Droppable droppableId="menu-list">
                                            {(provided) => (
                                                <div {...provided.droppableProps} ref={provided.innerRef}>
                                                    {config.menu && config.menu.map((item, index) => (
                                                        <Draggable key={index} draggableId={`menu-${index}`} index={index}>
                                                            {(provided) => (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    {...provided.dragHandleProps}
                                                                >
                                                                    <Card key={index} style={{ marginBottom: 16 }}>
                                                                        <Space vertical style={{ width: '100%' }}>
                                                                            <div style={{ marginBottom: 16 }}>
                                                                                <label style={{ display: 'block', marginBottom: 8 }}>文字</label>
                                                                                <Input
                                                                                    value={item.text}
                                                                                    onChange={value => handleMenuChange(index, 'text', value)}
                                                                                    placeholder="请输入菜单文本"
                                                                                />
                                                                            </div>
                                                                            <div style={{ marginBottom: 16 }}>
                                                                                <label style={{ display: 'block', marginBottom: 8 }}>图标</label>
                                                                                <ImageSelector
                                                                                    previewSize={24}
                                                                                    enableTDesignIcon={true}
                                                                                    value={item.icon ? item.icon : null}
                                                                                    onChange={value => handleImageSelect(index, 'icon', value)}
                                                                                />
                                                                            </div>
                                                                            <div style={{ marginBottom: 16 }}>
                                                                                <label style={{ display: 'block', marginBottom: 8 }}>选中图标</label>
                                                                                <ImageSelector
                                                                                    previewSize={24}
                                                                                    enableTDesignIcon={true}
                                                                                    value={item.active_icon ? item.active_icon : null}
                                                                                    onChange={value => handleImageSelect(index, 'active_icon', value)}
                                                                                />
                                                                            </div>
                                                                            <div style={{ marginBottom: 16 }}>
                                                                                <label style={{ display: 'block', marginBottom: 8 }}>页面</label>
                                                                                <LinkSelector
                                                                                    value={item.url}
                                                                                    onChange={value => handleMenuChange(index, 'url', value)}
                                                                                />
                                                                            </div>
                                                                            <Button type="danger" icon={<IconDelete />} onClick={() => handleRemoveMenu(index)}>删除</Button>
                                                                        </Space>
                                                                    </Card>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    ))}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                </TabPane>
                                <TabPane tab="其他设置" itemKey="basic">
                                    <div style={{ marginBottom: 16 }}>
                                        <label style={{ display: 'block', marginBottom: 8 }}>接口地址</label>
                                        <Input
                                            readonly={true}
                                            value={config.server_url}
                                            onChange={value => handleInputChange('server_url', value)}
                                            placeholder="无需修改"
                                        />
                                    </div>
                                    <div style={{ marginBottom: 16 }}>
                                        <label style={{ display: 'block', marginBottom: 8 }}>版权信息</label>
                                        <Input
                                            readonly={true}
                                            value={config.copyright}
                                            onChange={value => handleInputChange('copyright', value)}
                                            placeholder="无法修改"
                                        />
                                    </div>
                                    <div style={{ marginBottom: 16 }}>
                                        <label style={{ display: 'block', marginBottom: 8 }}>品牌Logo</label>
                                        <ImageSelector
                                            previewSize={48}
                                            enableTDesignIcon={false}
                                            value={config.logo}
                                            onChange={value => handleInputChange('logo', value)}
                                        />
                                    </div>
                                </TabPane>
                            </Tabs>
                            <div style={{ marginTop: 24, textAlign: 'center' }}>
                                <Button type="primary" htmlType="submit" loading={loading}>保存设置</Button>
                            </div>
                        </form>
                    </Card>
                </Col>
                <Col span={12} style={{ position: 'sticky', top: 24 }}>
                    <Card title="预览效果">
                        <Preview config={config} />
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default XcxTheme;